import { z } from "zod";

/**
 * Routing-classifier evaluation: metrics computed from per-document
 * predictions of an evaluation run. Labels are destination table ids; `null`
 * means "matches no routing rule" (the document belongs in review).
 */

export const classifierEvalModels = ["clef", "clef-flash"] as const;
export type ClassifierEvalModel = (typeof classifierEvalModels)[number];

/** Workers AI list price per million input tokens; Clef bills no output tokens. */
export const CLEF_INPUT_PRICE_PER_MILLION_USD: Record<ClassifierEvalModel, number> = {
  clef: 0.24,
  "clef-flash": 0.038,
};

export const NO_MATCH_LABEL = "__none__";

/** Below this many evaluated documents a threshold recommendation is noise. */
export const MIN_DOCUMENTS_FOR_THRESHOLD_RECOMMENDATION = 20;

export const classifierEvalWorkflowParamsSchema = z.object({
  evalRunId: z.string().min(1),
});
export type ClassifierEvalWorkflowParams = z.infer<typeof classifierEvalWorkflowParamsSchema>;

export type ClassifierEvalRouteSnapshot = {
  id: string;
  name: string;
  prompt: string;
  confidenceThreshold: number;
  tableId: string;
  tableName: string;
};

export type ClassifierEvalPrediction = {
  expectedTableId: string | null;
  /** Top-1 destination, or null when the model chose "no match". */
  predictedTableId: string | null;
  /** Score (0–100) of the top-1 answer, including the "no match" option. */
  topScore: number | null;
  /** Whether production routing (per-rule thresholds) would route automatically. */
  autoRouted: boolean;
  latencyMs: number | null;
  inputTokens: number | null;
  error: string | null;
};

export type ClassifierEvalLabelMetrics = {
  label: string;
  support: number;
  predicted: number;
  precision: number | null;
  recall: number | null;
  f1: number | null;
};

export type ClassifierEvalThresholdPoint = {
  threshold: number;
  /** Share of evaluated documents that would be routed automatically. */
  coverage: number;
  /** Share of automatically routed documents that went to the right table. */
  precision: number | null;
  misroutes: number;
};

export type ClassifierEvalMetrics = {
  total: number;
  evaluated: number;
  errors: number;
  top1Accuracy: number | null;
  autoRouteRate: number | null;
  autoRoutePrecision: number | null;
  misroutes: number;
  reviewRate: number | null;
  labels: ClassifierEvalLabelMetrics[];
  confusion: Array<{ expected: string; predicted: string; count: number }>;
  thresholdSweep: ClassifierEvalThresholdPoint[];
  /**
   * Lowest global threshold whose routed precision meets the target; null when
   * no threshold does or fewer than MIN_DOCUMENTS_FOR_THRESHOLD_RECOMMENDATION
   * documents were evaluated.
   */
  recommendedThreshold: number | null;
  recommendedPrecisionTarget: number;
  calibration: {
    expectedCalibrationError: number | null;
    bins: Array<{ from: number; to: number; count: number; meanScore: number | null; accuracy: number | null }>;
  };
  latencyMs: { p50: number | null; p95: number | null };
  inputTokens: number;
  estimatedCostUsd: number;
};

const ratio = (numerator: number, denominator: number) =>
  denominator === 0 ? null : numerator / denominator;

const labelOf = (tableId: string | null) => tableId ?? NO_MATCH_LABEL;

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const index = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1);
  return sorted[Math.max(0, index)] ?? null;
}

export function computeClassifierEvalMetrics(
  predictions: ClassifierEvalPrediction[],
  options: { model: ClassifierEvalModel; precisionTarget?: number },
): ClassifierEvalMetrics {
  const precisionTarget = options.precisionTarget ?? 0.98;
  const evaluated = predictions.filter((item) => item.error === null);
  const correct = (item: ClassifierEvalPrediction) =>
    item.predictedTableId === item.expectedTableId;

  const autoRouted = evaluated.filter((item) => item.autoRouted);
  const misroutes = autoRouted.filter((item) => !correct(item)).length;

  const labelSet = new Set<string>();
  for (const item of evaluated) {
    labelSet.add(labelOf(item.expectedTableId));
    labelSet.add(labelOf(item.predictedTableId));
  }
  const labels = [...labelSet].sort().map((label) => {
    const support = evaluated.filter((item) => labelOf(item.expectedTableId) === label).length;
    const predicted = evaluated.filter((item) => labelOf(item.predictedTableId) === label).length;
    const truePositives = evaluated.filter(
      (item) => labelOf(item.expectedTableId) === label && labelOf(item.predictedTableId) === label,
    ).length;
    const precision = ratio(truePositives, predicted);
    const recall = ratio(truePositives, support);
    const f1 =
      precision === null || recall === null || precision + recall === 0
        ? null
        : (2 * precision * recall) / (precision + recall);
    return { label, support, predicted, precision, recall, f1 };
  });

  const confusionCounts = new Map<string, number>();
  for (const item of evaluated) {
    const key = `${labelOf(item.expectedTableId)}\u0000${labelOf(item.predictedTableId)}`;
    confusionCounts.set(key, (confusionCounts.get(key) ?? 0) + 1);
  }
  const confusion = [...confusionCounts.entries()].map(([key, count]) => {
    const [expected = "", predicted = ""] = key.split("\u0000");
    return { expected, predicted, count };
  });

  // A global threshold applied to the top-1 rule score; "no match" never routes.
  const thresholdSweep: ClassifierEvalThresholdPoint[] = [];
  for (let threshold = 0; threshold <= 100; threshold += 5) {
    const routed = evaluated.filter(
      (item) =>
        item.predictedTableId !== null && item.topScore !== null && item.topScore >= threshold,
    );
    const wrong = routed.filter((item) => !correct(item)).length;
    thresholdSweep.push({
      threshold,
      coverage: evaluated.length === 0 ? 0 : routed.length / evaluated.length,
      precision: ratio(routed.length - wrong, routed.length),
      misroutes: wrong,
    });
  }
  const recommendedThreshold =
    evaluated.length < MIN_DOCUMENTS_FOR_THRESHOLD_RECOMMENDATION
      ? null
      : thresholdSweep.find((point) => point.precision !== null && point.precision >= precisionTarget)
          ?.threshold ?? null;

  const scored = evaluated.filter((item) => item.topScore !== null);
  const bins = Array.from({ length: 10 }, (_, index) => {
    const from = index * 10;
    const to = from + 10;
    const members = scored.filter((item) => {
      const value = item.topScore!;
      return index === 9 ? value >= from : value >= from && value < to;
    });
    return {
      from,
      to,
      count: members.length,
      meanScore: members.length
        ? members.reduce((sum, item) => sum + item.topScore!, 0) / members.length
        : null,
      accuracy: ratio(members.filter(correct).length, members.length),
    };
  });
  const expectedCalibrationError = scored.length
    ? bins.reduce(
        (sum, bin) =>
          bin.count && bin.meanScore !== null && bin.accuracy !== null
            ? sum + (bin.count / scored.length) * Math.abs(bin.meanScore / 100 - bin.accuracy)
            : sum,
        0,
      )
    : null;

  const latencies = evaluated
    .map((item) => item.latencyMs)
    .filter((value): value is number => value !== null)
    .sort((a, b) => a - b);
  const inputTokens = predictions.reduce((sum, item) => sum + (item.inputTokens ?? 0), 0);

  return {
    total: predictions.length,
    evaluated: evaluated.length,
    errors: predictions.length - evaluated.length,
    top1Accuracy: ratio(evaluated.filter(correct).length, evaluated.length),
    autoRouteRate: ratio(autoRouted.length, evaluated.length),
    autoRoutePrecision: ratio(autoRouted.length - misroutes, autoRouted.length),
    misroutes,
    reviewRate: ratio(evaluated.length - autoRouted.length, evaluated.length),
    labels,
    confusion,
    thresholdSweep,
    recommendedThreshold,
    recommendedPrecisionTarget: precisionTarget,
    calibration: { expectedCalibrationError, bins },
    latencyMs: { p50: percentile(latencies, 50), p95: percentile(latencies, 95) },
    inputTokens,
    estimatedCostUsd: (inputTokens / 1_000_000) * CLEF_INPUT_PRICE_PER_MILLION_USD[options.model],
  };
}
